import React, { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ROUTES } from "./routes";
import FullPageLoader from "../../common/components/FullPageLoader";
import { useIsAuthenticated } from "@azure/msal-react";
import ProtectedRoute from '../../common/components/ProtectedRoute';
import TimeSetupWrapper from "../TimeSetup";

// Tier 1: Critical (Always Loaded)
import Home from "../Home/Home";
import FastRacksReport from "../Reports/FastRacksReport";
import RackPricingWeekendHoliday from "../RackPricingWeekendHoliday";

// Tier 2: High-Frequency (Preload after 2s)
// Group all Tier 2 components into one chunk named "tier2"
const BidFeatures = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../Features/BidFeatures")
);
const RackFeatures = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../Features/RackFeatures")
);
const BidPricingFeatures = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../Features/BidPricingFeatures")
);
const RackPricingFeatures = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../Features/RackPricingFeatures")
);
const Setup = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../Features/Setup")
);
const RackPricing = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../RackPricing")
);
const BidPricing = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../BidPricing")
);
const Verify = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../Verify")
);
const RackConfirmation = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../RackConfirmation")
);
const RackConfirmationWeekendHoliday = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../RackConfirmationWeekendHoliday")
);
const BidConfirmation = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../BidConfirmation")
);
const DTNUpload = lazy(() =>
  import(/* webpackChunkName: "tier2" */ "../DTNUpload")
);

// Tier 3: Medium-Frequency (Load on demand)
const CalendarSchedule = lazy(() => import("../CalendarSchedule"));
const CustomerWrapper = lazy(() => import("../Customer"));
const DTNCity = lazy(() => import("../DTNCity"));
const FastRacksWrapper = lazy(() => import("../FastRacks"));
const FuelSupplierWrapper = lazy(() => import("../FuelSupplier"));
const FuelSupplierCostMap = lazy(() => import("../FuelSupplierCostMap"));
const OPISCity = lazy(() => import("../OPISCity"));
const PDIPricingSetup = lazy(() => import("../PDIPricingSetup"));
const PDISync = lazy(() => import("../PDISync"));
const FuelProduct = lazy(() => import("../FuelProduct/FuelProduct"));
const SettingWrapper = lazy(() => import("../Settings"));
const States = lazy(() => import("../States"));
const Tax = lazy(() => import("../Tax"));
const Terminal = lazy(() => import("../Terminal/Terminal"));
const TimeSetup = lazy(() => import("../TimeSetup"));
const StoredProcs = lazy(() => import("../StoredProcs"));
const VerifyBenchmarkWrapper = lazy(() => import("../VerifyBenchmark"));
const AllocationWrapper = lazy(() => import("../Allocation"));
const Outage = lazy(() => import("../Outage"));
const RackDefinition = lazy(() => import("../RackDefinition"));
const FormulaWrapper = lazy(() => import("../Formula"));
const TerminalGroupWrapper = lazy(() => import("../TerminalGroup"));
const RackNote = lazy(() => import("../RackNote"));
const RackPricingGroup = lazy(() => import("../RackPricingGroup"));
const SalesRep = lazy(() => import("../SalesRep"));
const CustomerRackEmail = lazy(() => import("../CustomerRackEmail"));
const CustomerRackSelection = lazy(() => import("../CustomerRackSelection"));
const FormulaOPIS = lazy(() => import("../FormulaOPIS"));
const RackMessaging = lazy(() => import("../RackMessaging"));
const BidDefinition = lazy(() => import("../BidDefinition"));
const BidFreight = lazy(() => import("../BidFreight"));
const CustomerPricingSetup = lazy(() => import("../CustomerPricingSetup"));
const BidTerminalGroupWrapper = lazy(() => import("../BidTerminalGroup"));
const CustomerBidEmail = lazy(() => import("../CustomerBidEmail"));
const WetMoveWrapper = lazy(() => import("../WetMove"));
const BenchmarkEntryWrapper = lazy(() => import("../BenchmarkEntry"));

// Tier 4: Low-Frequency (Load on demand)
const Reports = lazy(() => import("../Reports"));
const BidPriceFloor = lazy(() => import("../Reports/BidPriceFloor"));
const CustomerBidPricing = lazy(() => import("../Reports/CustomerBidPricing"));
const OpisLowReport = lazy(() => import("../Reports/OpisLowReport"));
const CustomerContractReport = lazy(() => import("../Reports/CustomerContractReport"));
const RackPricingReport = lazy(() => import("../Reports/RackPricingReport"));
const BidPricingReport = lazy(() => import("../Reports/BidPricingReport"));
const ProductSupplyReport = lazy(() => import("../Reports/ProductSupplyReport"));
const BidMarginReport = lazy(() => import("../Reports/BidMarginReport"));
const PDICostMarginReport = lazy(() => import("../Reports/PDICostMarginReport"));
const RackContractReport = lazy(() => import("../Reports/RackContractReport"));
const CustomerTerminalReport = lazy(() => import("../Reports/CustomerTerminalReport"));
const SumnerCountyReport = lazy(() => import("../Reports/SumnerCountyReport"));
const TransportPickListReport = lazy(() => import("../Reports/TransportPickListReport"));
const LogViewer = lazy(() => import("../LogViewer/LogViewer"));
const Quartz = lazy(() => import("../Quartz"));

const {
  RACK_PRICING,
  RACK_FEATURES,
  BID_PRICING,
  BID_FEATURES,
  REPORTS,
  SETUP,
} = ROUTES;

const CustomRoutes = () => {
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (isAuthenticated) {
      // Preload the entire Tier 2 chunk
      import(/* webpackChunkName: "tier2" */ "../Features/BidFeatures");
    }
  }, [isAuthenticated]);

  return (
    <Suspense fallback={<FullPageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path={ROUTES.home} element={<Home />} />
        <Route path={ROUTES.login} element={<Navigate to="/" />} />

        {/* Protected Routes */}
        <Route
          path={RACK_PRICING.verify}
          element={
            <ProtectedRoute path={RACK_PRICING.verify}>
              <Verify />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_PRICING.rackPricing}
          element={
            <ProtectedRoute path={RACK_PRICING.rackPricing}>
              <RackPricing />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_PRICING.rackConfirmation}
          element={
            <ProtectedRoute path={RACK_PRICING.rackConfirmation}>
              <RackConfirmation />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_PRICING.benchmarkEntry}
          element={
            <ProtectedRoute path={RACK_PRICING.benchmarkEntry}>
              <BenchmarkEntryWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_PRICING.wetMove}
          element={
            <ProtectedRoute path={RACK_PRICING.wetMove}>
              <WetMoveWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_PRICING.dtnUpload}
          element={
            <ProtectedRoute path={RACK_PRICING.dtnUpload}>
              <DTNUpload />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_PRICING.rackPricingWeekendHoliday}
          element={
            <ProtectedRoute path={RACK_PRICING.rackPricingWeekendHoliday}>
              <RackPricingWeekendHoliday />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_PRICING.rackConfirmationWeekendHoliday}
          element={
            <ProtectedRoute path={RACK_PRICING.rackConfirmationWeekendHoliday}>
              <RackConfirmationWeekendHoliday />
            </ProtectedRoute>
          }
        />

        {/* Rack Feature */}
        <Route
          path={RACK_FEATURES.allocation}
          element={
            <ProtectedRoute path={RACK_FEATURES.allocation}>
              <AllocationWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_FEATURES.outage}
          element={
            <ProtectedRoute path={RACK_FEATURES.outage}>
              <Outage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_FEATURES.rackDefinition}
          element={
            <ProtectedRoute path={RACK_FEATURES.rackDefinition}>
              <RackDefinition />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_FEATURES.formula}
          element={
            <ProtectedRoute path={RACK_FEATURES.formula}>
              <FormulaWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_FEATURES.terminalGroup}
          element={
            <ProtectedRoute path={RACK_FEATURES.terminalGroup}>
              <TerminalGroupWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_FEATURES.rackNote}
          element={
            <ProtectedRoute path={RACK_FEATURES.rackNote}>
              <RackNote />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_FEATURES.rackPricingGroup}
          element={
            <ProtectedRoute path={RACK_FEATURES.rackPricingGroup}>
              <RackPricingGroup />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_FEATURES.salesRep}
          element={
            <ProtectedRoute path={RACK_FEATURES.salesRep}>
              <SalesRep />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_FEATURES.customerRackEmail}
          element={
            <ProtectedRoute path={RACK_FEATURES.customerRackEmail}>
              <CustomerRackEmail />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_FEATURES.customerRackSelection}
          element={
            <ProtectedRoute path={RACK_FEATURES.customerRackSelection}>
              <CustomerRackSelection />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_FEATURES.formulaOPIS}
          element={
            <ProtectedRoute path={RACK_FEATURES.formulaOPIS}>
              <FormulaOPIS />
            </ProtectedRoute>
          }
        />
        <Route
          path={RACK_FEATURES.rackMessaging}
          element={
            <ProtectedRoute path={RACK_FEATURES.rackMessaging}>
              <RackMessaging />
            </ProtectedRoute>
          }
        />

        {/* Bid Pricing */}
        <Route
          path={BID_PRICING.bidPricing}
          element={
            <ProtectedRoute path={BID_PRICING.bidPricing}>
              <BidPricing />
            </ProtectedRoute>
          }
        />
        <Route
          path={BID_PRICING.bidConfirmation}
          element={
            <ProtectedRoute path={BID_PRICING.bidConfirmation}>
              <BidConfirmation />
            </ProtectedRoute>
          }
        />

        {/* Bid Feature */}
        <Route
          path={BID_FEATURES.bidDefinition}
          element={
            <ProtectedRoute path={BID_FEATURES.bidDefinition}>
              <BidDefinition />
            </ProtectedRoute>
          }
        />
        <Route
          path={BID_FEATURES.bidFreight}
          element={
            <ProtectedRoute path={BID_FEATURES.bidFreight}>
              <BidFreight />
            </ProtectedRoute>
          }
        />
        <Route
          path={BID_FEATURES.customerPricingSetup}
          element={
            <ProtectedRoute path={BID_FEATURES.customerPricingSetup}>
              <CustomerPricingSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path={BID_FEATURES.bidTerminalGroup}
          element={
            <ProtectedRoute path={BID_FEATURES.bidTerminalGroup} >
              <BidTerminalGroupWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path={BID_FEATURES.customerBidEmail}
          element={
            <ProtectedRoute path={BID_FEATURES.customerBidEmail}>
              <CustomerBidEmail />
            </ProtectedRoute>
          }
        />

        {/* Reports */}
        <Route
          path={REPORTS.bidPriceFloor}
          element={
            <ProtectedRoute path={REPORTS.bidPriceFloor}>
              <BidPriceFloor />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.customerBidPricing}
          element={
            <ProtectedRoute path={REPORTS.customerBidPricing}>
              <CustomerBidPricing />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.opisLowReport}
          element={
            <ProtectedRoute path={REPORTS.opisLowReport}>
              <OpisLowReport />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.customerContractReport}
          element={
            <ProtectedRoute path={REPORTS.customerContractReport}>
              <CustomerContractReport />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.rackPricingReport}
          element={
            <ProtectedRoute path={REPORTS.rackPricingReport}>
              <RackPricingReport />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.bidPricingReport}
          element={
            <ProtectedRoute path={REPORTS.bidPricingReport}>
              <BidPricingReport />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.rackContractReport}
          element={
            <ProtectedRoute path={REPORTS.rackContractReport}>
              <RackContractReport />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.customerTerminalReport}
          element={
            <ProtectedRoute path={REPORTS.customerTerminalReport}>
              <CustomerTerminalReport />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.sumnerCountyReport}
          element={
            <ProtectedRoute path={REPORTS.sumnerCountyReport}>
              <SumnerCountyReport />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.transportPickListReport}
          element={
            <ProtectedRoute path={REPORTS.transportPickListReport}>
              <TransportPickListReport />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.productSupplyReport}
          element={
            <ProtectedRoute path={REPORTS.productSupplyReport}>
              <ProductSupplyReport />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.bidMarginReport}
          element={
            <ProtectedRoute path={REPORTS.bidMarginReport}>
              <BidMarginReport />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.pdiCostMarginReport}
          element={
            <ProtectedRoute path={REPORTS.pdiCostMarginReport}>
              <PDICostMarginReport />
            </ProtectedRoute>
          }
        />
        <Route
          path={REPORTS.fastRacksReport}
          element={
            <ProtectedRoute path={REPORTS.fastRacksReport}>
              <FastRacksReport />
            </ProtectedRoute>
          }
        />

        {/* Setup */}
        <Route
          path={SETUP.calendarSchedule}
          element={
            <ProtectedRoute path={SETUP.calendarSchedule}>
              <CalendarSchedule />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.customer}
          element={
            <ProtectedRoute path={SETUP.customer}>
              <CustomerWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.dtnCity}
          element={
            <ProtectedRoute path={SETUP.dtnCity}>
              <DTNCity />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.fastRacks}
          element={
            <ProtectedRoute path={SETUP.fastRacks}>
              <FastRacksWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.fuelSupplier}
          element={
            <ProtectedRoute path={SETUP.fuelSupplier}>
              <FuelSupplierWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.fuelSupplierCostMap}
          element={
            <ProtectedRoute path={SETUP.fuelSupplierCostMap}>
              <FuelSupplierCostMap />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.opisCity}
          element={
            <ProtectedRoute path={SETUP.opisCity}>
              <OPISCity />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.pdiPricingSetup}
          element={
            <ProtectedRoute path={SETUP.pdiPricingSetup}>
              <PDIPricingSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.pdiSync}
          element={
            <ProtectedRoute path={SETUP.pdiSync}>
              <PDISync />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.fuelProduct}
          element={
            <ProtectedRoute path={SETUP.fuelProduct}>
              <FuelProduct />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.settings}
          element={
            <ProtectedRoute path={SETUP.settings}>
              <SettingWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.states}
          element={
            <ProtectedRoute path={SETUP.states}>
              <States />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.tax}
          element={
            <ProtectedRoute path={SETUP.tax}>
              <Tax />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.terminal}
          element={
            <ProtectedRoute path={SETUP.terminal}>
              <Terminal />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.timeSetup}
          element={
            <ProtectedRoute path={SETUP.timeSetup}>
              <TimeSetupWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.storedProcs}
          element={
            <ProtectedRoute path={SETUP.storedProcs}>
              <StoredProcs />
            </ProtectedRoute>
          }
        />
        <Route
          path={SETUP.verifyBenchmark}
          element={
            <ProtectedRoute path={SETUP.verifyBenchmark}>
              <VerifyBenchmarkWrapper />
            </ProtectedRoute>
          }
        />

        {/* Standalone */}
        <Route
          path={ROUTES.bidFeatures}
          element={
            <ProtectedRoute path={ROUTES.bidFeatures}>
              <BidFeatures />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.bidPricingFeatures}
          element={
            <ProtectedRoute path={ROUTES.bidPricingFeatures}>
              <BidPricingFeatures />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.quartz}
          element={
            <ProtectedRoute path={ROUTES.quartz}>
              <Quartz />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.logs}
          element={
            <ProtectedRoute path={ROUTES.logs}>
              <LogViewer />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.rackFeatures}
          element={
            <ProtectedRoute path={ROUTES.rackFeatures}>
              <RackFeatures />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.rackPricingFeatures}
          element={
            <ProtectedRoute path={ROUTES.rackPricingFeatures}>
              <RackPricingFeatures />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.reports}
          element={
            <ProtectedRoute path={ROUTES.reports}>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.setup}
          element={
            <ProtectedRoute path={ROUTES.setup}>
              <Setup />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
  );
};

export default CustomRoutes;
